import {
  DeleteOutlined,
  OrderedListOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import viLocale from "@fullcalendar/core/locales/vi";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Spin,
} from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useSelector } from "react-redux";
import ModalCreateTimesheet from "../../components/modal/ModalCreateTimesheet";
import DrawerPartTimeEmployees from "../../components/production/DrawerPartTimeEmployees";
import { TimesheetStatus } from "../../enum/common";
import { IEvent, IFilterTimesheet, IUpdateTask } from "../../models/Timesheet";
import {
  useDeleteTimesheet,
  useGetTimesheetWeekByDate,
  useUpdateTimesheet,
} from "../../query/TimesheetQuery";
import { RootState } from "../../store";
import { canEditTimesheet } from "../../utils/common";
import { notify } from "../../utils/toast";

const getStatusColor = (status: string) => {
  switch (status) {
    case TimesheetStatus.TODO:
      return "#fbbf24";
    case TimesheetStatus.IN_PROGRESS:
      return "#60a5fa";
    case TimesheetStatus.DONE:
      return "#34d399";
    default:
      return "#9ca3af";
  }
};

const Timesheet = () => {
  const userInfo = useSelector((state: RootState) => state.auth.userInfo);
  const canEdit = canEditTimesheet(userInfo);
  const [filterParams, setFilterParams] = useState<IFilterTimesheet>({
    startDate: dayjs().startOf("month").format("YYYY-MM-DD"),
    endDate: dayjs().endOf("month").format("YYYY-MM-DD"),
  });
  const [openCreateTask, setOpenCreateTask] = useState<boolean>(false);
  const [openEditDrawer, setOpenEditDrawer] = useState<boolean>(false);
  const [openPartTimeDrawer, setOpenPartTimeDrawer] = useState<boolean>(false);
  const [selectedEvent, setSelectedEvent] = useState<IEvent | null>(null);
  const [selectedDate, setSelectedDate] = useState<string>("");

  const [form] = Form.useForm();
  const { data: timeSheet, isLoading } =
    useGetTimesheetWeekByDate(filterParams);
  const updateTimesheet = useUpdateTimesheet();
  const deleteTimesheet = useDeleteTimesheet();
  const handleModalCancel = () => {
    setOpenCreateTask(false);
  };
  const handleAddSchedule = () => {
    setSelectedDate(dayjs().format("YYYY-MM-DD"));
    setOpenCreateTask(true);
  };

  const handleEventClick = (info: any) => {
    const event: IEvent = {
      id: info.event.id,
      resourceId: info.event.getResources()[0]?.id || "",
      title: info.event.title,
      start: info.event.start,
      end: info.event.end,
      extendedProps: info.event.extendedProps,
    };
    setSelectedEvent(event);
    form.setFieldsValue({
      taskName: event.title,
      description: event.extendedProps.description,
      dateRange: [dayjs(event.start), dayjs(event.end)],
      note: event.extendedProps.note,
      status: event.extendedProps.status,
    });
    setOpenEditDrawer(true);
  };

  const handleEditSave = async () => {
    try {
      const values = await form.validateFields();
      if (!selectedEvent) return;
      const [startDate, endDate] = values.dateRange;
      const startDateStr = startDate.format("YYYY-MM-DD");
      const endDateStr = endDate.format("YYYY-MM-DD");
      const payload: IUpdateTask = {
        taskName: values.taskName,
        description: values.description,
        note: values.note,
        startDate: new Date(`${startDateStr}T00:00:00Z`),
        endDate: new Date(`${endDateStr}T00:00:00Z`),
        status: values.status,
      };

      await updateTimesheet.mutateAsync({
        id: selectedEvent.id,
        data: payload,
      });
      notify.success("Cập nhật công việc thành công");
      setOpenEditDrawer(false);
      setSelectedEvent(null);
    } catch (error) {
      notify.error("Cập nhật công việc thất bại");
    }
  };

  const handleEditCancel = () => {
    setOpenEditDrawer(false);
    setSelectedEvent(null);
    form.resetFields();
  };

  return (
    <div className="relative h-full rounded shadow">
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/50 z-50 rounded">
          <Spin size="large" />
        </div>
      )}
      <div className="p-4">
        <div className="mb-4 flex justify-end">
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddSchedule}
            >
              Thêm lịch
            </Button>
            <Button
              type="primary"
              icon={<OrderedListOutlined />}
              onClick={() => setOpenPartTimeDrawer(true)}
            >
              DS Nhân viên Part Time
            </Button>
          </Space>
        </div>
        <FullCalendar
          schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
          plugins={[resourceTimelinePlugin, interactionPlugin]}
          initialView="resourceTimelineMonth"
          duration={{ days: 7 }}
          locale={viLocale}
          slotLabelFormat={{
            weekday: "long",
            day: "numeric",
            month: "numeric",
          }}
          datesSet={(info) => {
            const start = new Date(info.startStr);
            const end = new Date(info.endStr);
            setFilterParams({
              startDate: dayjs(start).format("YYYY-MM-DD"),
              endDate: dayjs(end).format("YYYY-MM-DD"),
            });
          }}
          resourceAreaHeaderContent="Nhân viên"
          resourceAreaWidth="200px"
          resources={timeSheet?.resources || []}
          events={
            timeSheet?.events?.map((event) => ({
              ...event,
              backgroundColor: getStatusColor(event.extendedProps.status),
              borderColor: getStatusColor(event.extendedProps.status),
            })) || []
          }
          height="auto"
          displayEventTime={false}
          dateClick={(info) => {
            setSelectedDate(info.dateStr);
            setOpenCreateTask(true);
          }}
          eventClick={handleEventClick}
        />
        <ModalCreateTimesheet
          open={openCreateTask}
          onCancel={handleModalCancel}
          onFinish={() => setOpenCreateTask(false)}
          selectedDate={selectedDate}
        />
        <DrawerPartTimeEmployees
          open={openPartTimeDrawer}
          onClose={() => setOpenPartTimeDrawer(false)}
        />
        <Drawer
          title={canEdit ? "Chỉnh sửa công việc" : "Chi tiết công việc"}
          placement="right"
          onClose={handleEditCancel}
          open={openEditDrawer}
          width={400}
        >
          {selectedEvent && (
            <>
              <div className="space-y-4 mb-6 pb-6 border-b">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs font-semibold uppercase mb-1">
                      Nhân viên
                    </div>
                    <div className="text-sm font-medium">
                      {selectedEvent.extendedProps.fullName}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase mb-1">
                      Phòng ban
                    </div>
                    <div className="text-sm font-medium">
                      {selectedEvent.extendedProps.departmentCode}
                    </div>
                  </div>
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase mb-1">
                    Loại nhân viên
                  </div>
                  <div className="text-sm font-medium">
                    {selectedEvent.extendedProps.memberType === "INTERNAL"
                      ? "Nội bộ"
                      : "Ngoài hệ thống"}
                  </div>
                </div>
              </div>

              <Form form={form} layout="vertical">
                <Form.Item
                  label="Tên công việc"
                  name="taskName"
                  rules={[
                    { required: true, message: "Vui lòng nhập tên công việc" },
                  ]}
                >
                  <Input disabled={!canEdit} placeholder="Nhập tên công việc" />
                </Form.Item>

                <Form.Item
                  label="Mô tả"
                  name="description"
                  rules={[{ required: true, message: "Vui lòng nhập mô tả" }]}
                >
                  <Input.TextArea
                    disabled={!canEdit}
                    placeholder="Nhập mô tả công việc"
                    rows={4}
                  />
                </Form.Item>

                <Form.Item
                  label="Khoảng thời gian"
                  name="dateRange"
                  rules={[
                    {
                      required: true,
                      message: "Vui lòng chọn khoảng thời gian",
                    },
                  ]}
                >
                  <DatePicker.RangePicker
                    disabled={!canEdit}
                    className="w-full"
                    format="YYYY-MM-DD"
                    placeholder={["Ngày bắt đầu", "Ngày kết thúc"]}
                  />
                </Form.Item>

                <Form.Item label="Ghi chú" name="note">
                  <Input.TextArea
                    disabled={!canEdit}
                    placeholder="Nhập ghi chú"
                    rows={3}
                  />
                </Form.Item>
                <Form.Item name="status" label="Trạng thái">
                  <Select
                    disabled={!canEdit}
                    placeholder="Chọn trạng thái"
                    options={[
                      { label: "Chưa làm", value: "TODO" },
                      { label: "Đang làm", value: "IN_PROGRESS" },
                      { label: "Hoàn thành", value: "DONE" },
                    ]}
                  />
                </Form.Item>

                <div className="flex gap-2">
                  <Button onClick={handleEditCancel} className="flex-1">
                    Thoát
                  </Button>
                  <Popconfirm
                    title="Xóa công việc"
                    description="Bạn có chắc chắn muốn xóa công việc này?"
                    onConfirm={async () => {
                      if (!selectedEvent) return;
                      await deleteTimesheet.mutateAsync(selectedEvent.id);
                      notify.success("Xóa công việc thành công");
                      setOpenEditDrawer(false);
                      setSelectedEvent(null);
                    }}
                    okText="Xóa"
                    cancelText="Hủy"
                    okButtonProps={{ danger: true }}
                  >
                    <Button
                      type="primary"
                      disabled={!canEdit}
                      icon={<DeleteOutlined />}
                      hidden={!canEdit}
                    >
                      Xoá
                    </Button>
                  </Popconfirm>
                  <Button
                    type="primary"
                    onClick={handleEditSave}
                    loading={updateTimesheet.isPending}
                    className="flex-1"
                    icon={<SaveOutlined />}
                    disabled={!canEdit}
                    hidden={!canEdit}
                  >
                    Lưu
                  </Button>
                </div>
              </Form>
            </>
          )}
        </Drawer>
      </div>
    </div>
  );
};

export default Timesheet;
